// Presentational admin panels extracted from Admin.js so the page component
// stays small and focused on state + handlers. Pure props in, JSX out.
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Gift, Paperclip } from "lucide-react";

export const StatCard = ({ label, value, testId }) => (
  <Card className="rounded-2xl"><CardContent className="p-4">
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-2xl font-bold mt-1" data-testid={testId}>{value ?? "—"}</p>
  </CardContent></Card>
);

export const AdminUsersTable = ({ users, onChangeRole, onChangePlan, onToggleActive, onGift }) => (
  <Card className="rounded-2xl"><CardContent className="p-0 overflow-x-auto">
    <Table>
      <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>Plan</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
      <TableBody>
        {users.map((u) => (
          <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
            <TableCell className="font-medium">{u.name}</TableCell>
            <TableCell className="text-muted-foreground">{u.email}</TableCell>
            <TableCell>
              <Select value={u.role} onValueChange={(v) => onChangeRole(u, v)}>
                <SelectTrigger className="w-[130px] h-8" data-testid={`admin-role-select-${u.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{["owner", "caregiver", "provider", "admin"].map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
              </Select>
            </TableCell>
            <TableCell>
              <Select value={["basic", "pro", "premium"].includes(u.plan) ? u.plan : "basic"} onValueChange={(v) => onChangePlan(u, v)}>
                <SelectTrigger className="w-[110px] h-8" data-testid={`admin-plan-select-${u.id}`}><SelectValue /></SelectTrigger>
                <SelectContent>{["basic", "pro", "premium"].map((p) => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}</SelectContent>
              </Select>
            </TableCell>
            <TableCell><Badge variant="secondary" className={`border-0 ${u.isActive ? "bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))]" : "bg-[hsl(var(--destructive)/0.12)] text-[hsl(var(--destructive))]"}`}>{u.isActive ? "active" : "disabled"}</Badge></TableCell>
            <TableCell className="text-right">
              <div className="flex gap-1.5 justify-end">
                <Button size="sm" variant="outline" className="rounded-lg" onClick={() => onGift(u)} data-testid={`admin-gift-user-${u.id}`}><Gift className="w-3.5 h-3.5" /></Button>
                <Button size="sm" variant="secondary" className="rounded-lg" onClick={() => onToggleActive(u)} data-testid={`admin-toggle-user-${u.id}`}>{u.isActive ? "Disable" : "Enable"}</Button>
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </CardContent></Card>
);

export const AdminPetsTable = ({ pets }) => (
  <Card className="rounded-2xl"><CardContent className="p-0 overflow-x-auto">
    <Table>
      <TableHeader><TableRow><TableHead>Pet</TableHead><TableHead>Species / Breed</TableHead><TableHead>Owner</TableHead><TableHead>Owner Email</TableHead><TableHead>Owner Plan</TableHead><TableHead>Added</TableHead></TableRow></TableHeader>
      <TableBody>
        {pets.map((p) => (
          <TableRow key={p.id} data-testid={`admin-pet-row-${p.id}`}>
            <TableCell className="font-medium">{p.name}</TableCell>
            <TableCell className="text-muted-foreground capitalize">{p.species}{p.breed ? ` · ${p.breed}` : ""}</TableCell>
            <TableCell>{p.ownerName}</TableCell>
            <TableCell className="text-muted-foreground text-xs">{p.ownerEmail}</TableCell>
            <TableCell><Badge variant="secondary" className="border-0 capitalize">{p.ownerPlan}</Badge></TableCell>
            <TableCell className="text-xs text-muted-foreground">{new Date(p.createdAt).toLocaleDateString()}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    {!pets.length && <p className="text-sm text-muted-foreground p-6">No pets on the platform yet.</p>}
  </CardContent></Card>
);

export const AdminRevenuePanel = ({ revenue, onSendDigest }) => (
  <>
    <div className="flex justify-end mb-3">
      <Button size="sm" variant="outline" className="rounded-lg" onClick={onSendDigest} data-testid="admin-send-digest-button">
        Send weekly digest now
      </Button>
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
      <StatCard label="Total revenue (all time)" value={revenue ? `$${revenue.totalRevenue}` : null} testId="admin-revenue-total" />
      <StatCard label="This month" value={revenue ? `$${revenue.revenueThisMonth}` : null} testId="admin-revenue-month" />
      <StatCard label="MRR" value={revenue ? `$${revenue.mrr}` : null} testId="admin-revenue-mrr" />
      <StatCard label="Trials converting soon" value={revenue?.trialingCount} testId="admin-revenue-trialing" />
    </div>
    <div className="grid sm:grid-cols-3 gap-3 mb-4">
      {revenue && Object.entries(revenue.subscribersByPlan).map(([plan, count]) => (
        <Card key={plan} className="rounded-2xl"><CardContent className="p-4 flex items-center justify-between">
          <div><p className="text-xs text-muted-foreground capitalize">{plan} subscribers</p><p className="text-xl font-bold mt-0.5" data-testid={`admin-subs-${plan}`}>{count}</p></div>
          <Badge variant="secondary" className="border-0">${revenue.planPrices?.[plan]}/mo</Badge>
        </CardContent></Card>
      ))}
    </div>
    <Card className="rounded-2xl"><CardContent className="p-0 overflow-x-auto">
      <Table>
        <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Email</TableHead><TableHead>Package</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
        <TableBody>
          {(revenue?.recentTransactions || []).map((tx) => (
            <TableRow key={tx.id} data-testid={`admin-tx-row-${tx.id}`}>
              <TableCell className="text-xs text-muted-foreground">{new Date(tx.createdAt).toLocaleString()}</TableCell>
              <TableCell className="text-xs">{tx.email}</TableCell>
              <TableCell className="text-xs capitalize">{(tx.packageId || "").replace("_", " ")}</TableCell>
              <TableCell className="font-medium">${tx.amount}</TableCell>
              <TableCell><Badge variant="secondary" className={`border-0 capitalize ${tx.paymentStatus === "paid" ? "bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))]" : ""}`}>{tx.paymentStatus}</Badge></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {!(revenue?.recentTransactions || []).length && <p className="text-sm text-muted-foreground p-6">No transactions yet.</p>}
    </CardContent></Card>
  </>
);

export const AdminTicketsPanel = ({ tickets, onReply, onUpdateStatus, onOpenAttachment }) => (
  <div className="space-y-3">
    {!tickets.length && <p className="text-sm text-muted-foreground">No support tickets.</p>}
    {tickets.map((t) => (
      <Card key={t.id} className="rounded-2xl" data-testid={`admin-ticket-${t.id}`}>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
            <div className="flex-1">
              <div className="flex gap-1.5 items-center flex-wrap">
                <p className="font-medium text-sm">{t.subject}</p>
                <Badge variant="secondary" className={`border-0 capitalize ${t.priority === "urgent" ? "bg-[hsl(var(--destructive)/0.12)] text-[hsl(var(--destructive))]" : t.priority === "high" ? "bg-[hsl(var(--warning)/0.14)] text-[hsl(var(--warning))]" : ""}`}>{t.priority}</Badge>
                <Badge variant="secondary" className="border-0 capitalize">{t.status?.replace("_", " ")}</Badge>
                {t.category && <Badge variant="secondary" className="border-0 capitalize text-[10px]">{t.category.replace("_", " ")}</Badge>}
              </div>
              <p className="text-xs text-muted-foreground mt-1">{t.description?.slice(0, 200)} · {new Date(t.createdAt).toLocaleString()}</p>
              {(t.attachmentIds || []).length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {t.attachmentIds.map((id) => (
                    <button key={id} onClick={() => onOpenAttachment(id)} className="inline-flex items-center gap-1 text-xs bg-secondary hover:bg-secondary/70 rounded-lg px-2 py-1" data-testid={`admin-ticket-attachment-${id}`}>
                      <Paperclip className="w-3 h-3" /> Attachment {id.slice(0, 8)}
                    </button>
                  ))}
                </div>
              )}
              {(t.replies || []).length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {t.replies.map((r, i) => (
                    <p key={`${r.at || "reply"}-${i}`} className="text-xs bg-[hsl(var(--primary)/0.06)] rounded-lg px-2.5 py-1.5"><span className="font-semibold text-primary">{r.byName}:</span> {r.message}</p>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" variant="outline" className="rounded-lg" onClick={() => onReply(t)} data-testid={`admin-ticket-reply-${t.id}`}>Reply</Button>
              {t.status === "open" && <Button size="sm" variant="secondary" className="rounded-lg" onClick={() => onUpdateStatus(t, "in_progress")} data-testid={`admin-ticket-progress-${t.id}`}>Take</Button>}
              {t.status !== "resolved" && t.status !== "closed" && <Button size="sm" className="rounded-lg" onClick={() => onUpdateStatus(t, "resolved")} data-testid={`admin-ticket-resolve-${t.id}`}>Resolve</Button>}
            </div>
          </div>
        </CardContent>
      </Card>
    ))}
  </div>
);

export const AdminErrorsTable = ({ errors }) => (
  <Card className="rounded-2xl"><CardContent className="p-0 overflow-x-auto">
    <Table>
      <TableHeader><TableRow><TableHead>Time</TableHead><TableHead>Action</TableHead><TableHead>User</TableHead><TableHead>Status</TableHead><TableHead>What they saw</TableHead><TableHead>Where</TableHead></TableRow></TableHeader>
      <TableBody>
        {errors.map((e) => (
          <TableRow key={e.id} data-testid={`admin-error-row-${e.id}`}>
            <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{new Date(e.createdAt).toLocaleString()}</TableCell>
            <TableCell><Badge variant="secondary" className="border-0 text-[10px]">{e.action}</Badge></TableCell>
            <TableCell className="text-xs">{e.email || <span className="text-muted-foreground">anonymous</span>}</TableCell>
            <TableCell>{e.status ? <Badge variant="secondary" className={`border-0 ${e.status >= 500 ? "bg-[hsl(var(--destructive)/0.12)] text-[hsl(var(--destructive))]" : "bg-[hsl(var(--warning)/0.14)] text-[hsl(var(--warning))]"}`}>{e.status}</Badge> : "—"}</TableCell>
            <TableCell className="text-xs max-w-[320px]"><span className="line-clamp-2">{e.message}</span></TableCell>
            <TableCell className="text-xs text-muted-foreground">{e.path || "—"}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    {!errors.length && <p className="text-sm text-muted-foreground p-6">No client errors reported — quiet skies.</p>}
  </CardContent></Card>
);


export const AdminAuditTable = ({ audit }) => (
  <Card className="rounded-2xl"><CardContent className="p-0 overflow-x-auto">
    <Table>
      <TableHeader><TableRow><TableHead>Action</TableHead><TableHead>Entity</TableHead><TableHead>User</TableHead><TableHead>Time</TableHead></TableRow></TableHeader>
      <TableBody>
        {audit.map((a) => (
          <TableRow key={a.id}>
            <TableCell className="font-medium text-xs">{a.action}</TableCell>
            <TableCell className="text-xs text-muted-foreground">{a.entityType}</TableCell>
            <TableCell className="text-xs text-muted-foreground">{a.userId?.slice(0, 12) || "—"}</TableCell>
            <TableCell className="text-xs text-muted-foreground">{new Date(a.createdAt).toLocaleString()}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </CardContent></Card>
);

export const AdminRunsPanel = ({ runs }) => (
  <div className="space-y-3">
    {!runs.length && <p className="text-sm text-muted-foreground">No agent runs yet.</p>}
    {runs.map((r) => (
      <Card key={r.id} className="rounded-2xl">
        <CardContent className="p-4">
          <div className="flex gap-1.5 items-center flex-wrap">
            <Badge variant="secondary" className="border-0 capitalize">{r.agentKey?.replace(/_/g, " ")}</Badge>
            <Badge variant="secondary" className="border-0 capitalize">{r.status?.replace(/_/g, " ")}</Badge>
            <Badge variant="secondary" className="border-0">confidence: {r.confidence}</Badge>
            <Badge variant="secondary" className="border-0">risk: {r.riskLevel}</Badge>
            <span className="text-xs text-muted-foreground ml-auto">{new Date(r.createdAt).toLocaleString()}</span>
          </div>
          <p className="text-xs text-muted-foreground mt-2">Input: {r.input?.slice(0, 160)}</p>
          <p className="text-sm mt-1 line-clamp-3">{r.output}</p>
        </CardContent>
      </Card>
    ))}
  </div>
);

export const AdminKbPanel = ({ kb, onAdd }) => (
  <>
    <div className="flex justify-end mb-3"><Button onClick={onAdd} className="rounded-xl" data-testid="admin-add-kb-button">Add article</Button></div>
    <div className="grid sm:grid-cols-2 gap-4">
      {kb.map((a) => (
        <Card key={a.id} className="rounded-2xl">
          <CardContent className="p-4">
            <div className="flex items-center justify-between"><p className="font-medium text-sm">{a.title}</p><Badge variant="secondary" className="border-0 capitalize">{a.category}</Badge></div>
            <p className="text-xs text-muted-foreground mt-1.5 line-clamp-3">{a.content}</p>
          </CardContent>
        </Card>
      ))}
    </div>
  </>
);
